import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiClient } from '../api/client';
import { DOMAIN_PARAM, domainOverrideEnabled } from '../api/fairDomain';
import { useFair } from '../api/FairProvider';
import { useApi } from '../api/useApi';
import { AsyncBoundary } from '../components/AsyncState';
import { FairsModal } from '../components/FairsModal';
import { paths } from '../routes';
import { siblingFlagUrl } from '../utils/flag';
import { formatDateRange } from '../utils/format';
import { accentBackground, accentFor, accentVariable } from '../utils/pavilion-accent';

export function HomePage(): ReactElement {
  const { t, i18n } = useTranslation();
  const [fairsOpen, setFairsOpen] = useState(false);
  const { fair, siblings, currentSibling } = useFair();
  const pavilions = useApi(() => apiClient.getPavilions());

  return (
    <div className="app-shell pb-12">
      <section className="pt-6 pb-8 text-center">
        {/* The full mark is the lockup: symbol plus wordmark, so it is the h1. */}
        <h1>
          <img
            src={fair?.logoUrl ?? '/assets/logo-full.svg'}
            alt={fair?.name ?? ''}
            className="mx-auto h-auto w-56 max-w-full object-contain"
          />
        </h1>
        {fair !== undefined && (
          <p className="mt-4 inline-block rounded-full bg-white/8 px-5 py-2 text-fog-300">
            {formatDateRange(fair.startsAt, fair.endsAt, i18n.language)}
          </p>
        )}

        {/* Each fair is its own deployment, so a flag opens that country's site
            in a new tab rather than switching data in place.

            TESTING ONLY: with VITE_ALLOW_DOMAIN_OVERRIDE set, a flag instead
            repoints this deployment at that fair via `?domain=`, in the same
            tab. Mirrors FairsModal — both switchers must agree, or one would
            leave the other's stored fair behind. */}
        <ul className="mt-8 flex items-center justify-center gap-4">
          {siblings.map((sibling) => {
            const overrideHref =
              domainOverrideEnabled && sibling.domain !== null
                ? `/?${DOMAIN_PARAM}=${encodeURIComponent(sibling.domain)}`
                : null;

            return (
              <li key={sibling.id}>
                <a
                  href={overrideHref ?? sibling.url ?? '#'}
                  {...(overrideHref === null && {
                    target: '_blank',
                    rel: 'noreferrer noopener',
                  })}
                  aria-label={sibling.name}
                  aria-current={sibling.isCurrent ? 'true' : undefined}
                  className={`block rounded-full transition-opacity ${
                    sibling.isCurrent
                      ? // The ring sits away from the disc, so the gap is padding on the
                        // link itself rather than a ring offset.
                        'p-1.5 ring-2 ring-fog-500'
                      : // Unselected fairs are dimmed; hover brings them back to full.
                        'p-1.5 opacity-45 hover:opacity-100'
                  }`}
                >
                  <img
                    src={siblingFlagUrl(sibling)}
                    alt=""
                    className="size-8 rounded-full object-cover"
                  />
                </a>
              </li>
            );
          })}
        </ul>

        <p className="mt-4 text-fog-300">
          {t('home.exhibitors')}{' '}
          <button
            type="button"
            onClick={() => {
              setFairsOpen(true);
            }}
            className="font-bold text-fog-100 underline-offset-4 hover:underline"
          >
            {currentSibling?.name ?? ''}
          </button>
        </p>
      </section>

      <AsyncBoundary loading={pavilions.loading} error={pavilions.error} onRetry={pavilions.reload}>
        <ul className="space-y-6">
          {(pavilions.data ?? []).map((pavilion) => (
            <li key={pavilion.id}>
              <Link
                to={paths.category(pavilion.slug)}
                className="card relative block overflow-hidden border border-ink-700 px-5 py-7 text-center transition-colors hover:bg-ink-800"
              >
                {/* The bar sits inside the card, flush with its top edge, and bleeds a
                  wash of its own colour down into the card behind the text. */}
                <span
                  aria-hidden="true"
                  className={`absolute top-0 left-1/2 h-2 w-[70px] -translate-x-1/2 rounded-b-md ${accentBackground(
                    accentFor(pavilion.position),
                  )}`}
                />
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 h-24"
                  style={{
                    background: `radial-gradient(60% 100% at 50% 0%, color-mix(in srgb, ${accentVariable(
                      accentFor(pavilion.position),
                    )} 22%, transparent) 0%, transparent 100%)`,
                  }}
                />
                <h2 className="relative text-2xl font-bold text-fog-100">{pavilion.name}</h2>
                <p className="relative mt-2 text-fog-500">{pavilion.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      </AsyncBoundary>

      <FairsModal
        open={fairsOpen}
        onClose={() => {
          setFairsOpen(false);
        }}
      />
    </div>
  );
}
